import { useState } from "react";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { CONTACT } from "@/data/content";
import { toast } from "@/components/ui/sonner";

const API = "https://forms.elido.nu/api";
const SITE_ID = "icidentity";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = { name: "", organization: "", email: "", phone: "", message: "", website: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const setField = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Ange ditt namn.";
    if (!form.organization.trim()) next.organization = "Ange din organisation.";
    if (!form.email.trim()) next.email = "Ange din e-postadress.";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "Ange en giltig e-postadress.";
    if (!form.message.trim()) next.message = "Skriv ett kort meddelande.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    if (!validate()) return;
    setStatus("sending");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site_id: SITE_ID,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          message: `Organisation: ${form.organization.trim()}\n\n${form.message.trim()}`,
          website: form.website,
        }),
      });
      if (!res.ok) throw new Error("request_failed");
      setStatus("success");
      setForm(EMPTY);
      toast.success("Tack! Din förfrågan har skickats.");
    } catch (err) {
      setStatus("error");
      toast.error("Något gick fel. Försök igen eller mejla oss direkt.");
    }
  };

  return (
    <section id="kontakt" className="scroll-mt-24 border-t border-brand-line bg-white">
      <div className="mx-auto max-w-6xl px-6 py-24 md:py-32 lg:px-8">
        <div className="grid grid-cols-1 gap-14 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <p className="eyebrow">
                <span className="ic-triangle" aria-hidden="true" />
                Kontakt
              </p>
              <h2 className="mt-7 font-display text-3xl font-normal leading-[1.1] tracking-[-0.01em] text-brand-ink md:text-5xl">
                Börja med ett samtal
              </h2>
              <p className="mt-6 max-w-md text-lg leading-relaxed text-brand-muted">
                Bra lösningar börjar ofta med en enkel diskussion. Berätta vad ni vill förändra,
                förbättra eller lösa inom identitet och åtkomst, så delar vi gärna med oss av våra
                erfarenheter och tankar kring nästa steg.
              </p>
              <div className="mt-10 space-y-4 border-t border-brand-line pt-8">
                <div>
                  <p className="field-label mb-1">E-post</p>
                  <a
                    href={CONTACT.emailHref}
                    className="text-lg text-brand-ink transition-colors hover:text-brand-blue"
                    data-testid="contact-email"
                  >
                    {CONTACT.email}
                  </a>
                </div>
                <div>
                  <p className="field-label mb-1">Telefon</p>
                  <a
                    href={CONTACT.phoneHref}
                    className="text-lg text-brand-ink transition-colors hover:text-brand-blue"
                    data-testid="contact-phone"
                  >
                    {CONTACT.phone}
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <Reveal delay={0.08}>
              {status === "success" ? (
                <div
                  className="flex flex-col items-start border border-brand-line bg-white p-10"
                  data-testid="contact-success"
                  role="status"
                >
                  <span className="flex h-11 w-11 items-center justify-center bg-brand-blue text-white">
                    <Check className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-display text-2xl text-brand-ink">Tack för din förfrågan.</h3>
                  <p className="mt-3 text-brand-muted">
                    Vi har tagit emot ditt meddelande och återkommer så snart vi kan.
                  </p>
                  <button
                    type="button"
                    className="btn-line mt-8"
                    onClick={() => setStatus("idle")}
                    data-testid="contact-reset"
                  >
                    Skicka en till förfrågan
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate data-testid="contact-form">
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="website">Lämna detta fält tomt</label>
                    <input
                      id="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={setField("website")}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="field-label">
                        Namn
                      </label>
                      <input
                        id="name"
                        type="text"
                        className="field-input"
                        value={form.name}
                        onChange={setField("name")}
                        aria-invalid={!!errors.name}
                        autoComplete="name"
                        data-testid="contact-name"
                      />
                      {errors.name && (
                        <p className="mt-1.5 text-sm text-destructive" data-testid="error-name">
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="organization" className="field-label">
                        Organisation
                      </label>
                      <input
                        id="organization"
                        type="text"
                        className="field-input"
                        value={form.organization}
                        onChange={setField("organization")}
                        aria-invalid={!!errors.organization}
                        autoComplete="organization"
                        data-testid="contact-organization"
                      />
                      {errors.organization && (
                        <p className="mt-1.5 text-sm text-destructive" data-testid="error-organization">
                          {errors.organization}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="email" className="field-label">
                        E-post
                      </label>
                      <input
                        id="email"
                        type="email"
                        className="field-input"
                        value={form.email}
                        onChange={setField("email")}
                        aria-invalid={!!errors.email}
                        autoComplete="email"
                        data-testid="contact-email-input"
                      />
                      {errors.email && (
                        <p className="mt-1.5 text-sm text-destructive" data-testid="error-email">
                          {errors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="phone" className="field-label">
                        Telefon <span className="font-normal normal-case tracking-normal text-brand-muted/70">(frivilligt)</span>
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        className="field-input"
                        value={form.phone}
                        onChange={setField("phone")}
                        autoComplete="tel"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label htmlFor="message" className="field-label">
                      Meddelande
                    </label>
                    <textarea
                      id="message"
                      rows={5}
                      className="field-input h-auto py-3"
                      value={form.message}
                      onChange={setField("message")}
                      aria-invalid={!!errors.message}
                      data-testid="contact-message"
                    />
                    {errors.message && (
                      <p className="mt-1.5 text-sm text-destructive" data-testid="error-message">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  {status === "error" && (
                    <p className="mt-6 border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive" data-testid="contact-error" role="alert">
                      Något gick fel när förfrågan skulle skickas. Försök igen, eller mejla oss på {CONTACT.email}.
                    </p>
                  )}

                  <button
                    type="submit"
                    className="btn-primary group mt-8 w-full sm:w-auto"
                    disabled={status === "sending"}
                    data-testid="contact-submit"
                  >
                    {status === "sending" ? (
                      <>
                        Skickar
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </>
                    ) : (
                      <>
                        Skicka förfrågan
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
